import React, {
  createContext, useContext, useEffect, useState, useCallback
} from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import type { Profile, UserRole } from '@/lib/types'

interface AuthContextValue {
  session:    Session | null
  user:       User    | null
  profile:    Profile | null
  loading:    boolean
  isAdmin:    boolean
  isStaff:    boolean
  hasRole:    (role: UserRole) => boolean
  signIn:     (email: string, password: string) => Promise<void>
  signOut:    () => Promise<void>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

/** Only these two verified profile IDs may receive full administrator access. */
const ADMIN_PROFILE_IDS = new Set([
  'f98db9c6-fea3-4931-b03a-1d2c37459f3e', // Denise Shorter
  '37a99010-e82e-43fb-8f0b-2fa91b6c66dd', // Dria M
])
const STAFF_ROLES: UserRole[] = [
  'executive_director','operations_manager','program_manager',
  'staff','volunteer_coordinator','employee'
]

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession]   = useState<Session | null>(null)
  const [user,    setUser]      = useState<User | null>(null)
  const [profile, setProfile]   = useState<Profile | null>(null)
  const [loading, setLoading]   = useState(true)

  const loadProfile = useCallback(async (userId: string) => {
    const { data, error } = await supabase.from('profiles')
      .select('*').eq('id', userId).single()
    if (!error && data) setProfile(data as Profile)
  }, [])

  const refreshProfile = useCallback(async () => {
    if (user) await loadProfile(user.id)
  }, [user, loadProfile])

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      if (session?.user) {
        loadProfile(session.user.id).finally(() => setLoading(false))
      } else {
        setLoading(false)
      }
    })

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session)
        setUser(session?.user ?? null)
        if (session?.user) {
          await loadProfile(session.user.id)
          // Update last_login
          await supabase.from('profiles')
            .update({ last_login: new Date().toISOString() })
            .eq('id', session.user.id)
        } else {
          setProfile(null)
        }
        setLoading(false)
      }
    )

    return () => subscription.unsubscribe()
  }, [loadProfile])

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }, [])

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    setProfile(null)
  }, [])

  // A role label or editable profile flag alone must never grant full admin access.
  // The database policy/migration mirrors this allowlist; this is the UI-side guard.
  const isAdmin = profile ? ADMIN_PROFILE_IDS.has(profile.id) && profile.is_admin === true && profile.is_active === true : false
  const isStaff = profile ? STAFF_ROLES.includes(profile.role) : false
  const hasRole = useCallback(
    (role: UserRole) => profile?.role === role || isAdmin,
    [profile?.role, isAdmin]
  )

  return (
    <AuthContext.Provider value={{
      session, user, profile, loading,
      isAdmin, isStaff, hasRole,
      signIn, signOut, refreshProfile,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
